import React, { useState, useEffect } from 'react';
import './LibraryModule.css';
import LibraryCatalog from './LibraryCatalog';
import BookDetail from './BookDetail';
import BorrowingSystem from './BorrowingSystem';
import ReaderProfile from './ReaderProfile';
import LibraryReports from './LibraryReports';
import OPACInterface from './OPACInterface';
// A2A Library Service - fully migrated from direct Supabase
import { a2aLibraryService } from '../../a2a/services/a2aLibraryService.js';

const LibraryModule = ({ currentUser, onBack, onTabChange, role = 'parent' }) => {
  // Hide admin features for parents
  const showAdminFeatures = role !== 'parent';
  
  const [currentView, setCurrentView] = useState('catalog'); // 'catalog', 'detail', 'borrowing', 'profile', 'reports', 'opac'
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedReader, setSelectedReader] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState('tn_01'); // Default tenant
  const [error, setError] = useState(null);
  const [cacheVersion, setCacheVersion] = useState(Date.now()); // Cache busting

  // Library data from Supabase
  const [books, setBooks] = useState([]);
  const [readers, setReaders] = useState([]);
  const [borrowings, setBorrowings] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (role === 'parent' && currentUser?.children) {
      // Filter to show only children's borrowings
      fetchChildrenBorrowings(currentUser.children);
    } else {
      fetchLibraryData();
    }
  }, [currentTenant, currentUser, role]);

  const fetchLibraryData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('=== LibraryModule: Starting data fetch ===');
      console.log('Current tenant:', currentTenant);
      console.log('Cache busting timestamp:', Date.now());
      
      // Fetch data using A2A library service
      const [booksResponse, readersResponse, borrowingsResponse] = await Promise.all([
        a2aLibraryService.getBooks({ tenant_id: currentTenant }),
        a2aLibraryService.getReaders({ tenant_id: currentTenant }),
        a2aLibraryService.getBorrowHistory({ tenant_id: currentTenant })
      ]);

      if (booksResponse.success) {
        setBooks(booksResponse.data || []);
      } else {
        console.error('Error fetching books:', booksResponse.error);
        setError(`Error fetching books: ${booksResponse.error?.message}`);
      }

      if (readersResponse.success) {
        console.log('=== LibraryModule Debug ===');
        console.log('Readers response success:', readersResponse.success);
        console.log('Readers data received:', readersResponse.data);
        console.log('Readers count:', readersResponse.data?.length);
        if (readersResponse.data && readersResponse.data.length > 0) {
          console.log('First reader:', readersResponse.data[0]);
          console.log('All readers display names:', readersResponse.data.map(r => ({
            reader_number: r.reader_number,
            display_name: r.display_name,
            student_number: r.student_number,
            class_info: r.class_info
          })));
        }
        setReaders(readersResponse.data || []);
      } else {
        console.error('Error fetching readers:', readersResponse.error);
        setError(`Error fetching readers: ${readersResponse.error?.message}`);
      }

      if (borrowingsResponse.success) {
        setBorrowings(borrowingsResponse.data || []);
      } else {
        console.error('Error fetching borrowings:', borrowingsResponse.error);
        setError(`Error fetching borrowings: ${borrowingsResponse.error?.message}`);
      }

      // For now, categories are shared across tenants, so we can fetch without tenant filter
      // In a real implementation, you might want to fetch categories per tenant
      // Extract unique categories from books data
      const uniqueCategories = [...new Set(booksResponse.data?.map(book => book.category) || [])];
      setCategories(uniqueCategories);

    } catch (error) {
      console.error('Error fetching library data:', error);
      setError(`Error fetching library data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    setSelectedBook(null);
    setSelectedReader(null);
  };

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setCurrentView('detail');
  };

  const handleReaderSelect = (reader) => {
    setSelectedReader(reader);
    setCurrentView('profile');
  };

  const handleBackToCatalog = () => {
    setCurrentView('catalog');
    setSelectedBook(null);
    setSelectedReader(null);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      try {
        const response = await a2aLibraryService.searchBooks({
          query,
          tenant_id: currentTenant
        });
        if (response.success) {
          setBooks(response.data || []);
        }
      } catch (error) {
        console.error('Error searching books:', error);
      }
    } else {
      // If search is empty, fetch all books
      fetchLibraryData();
    }
  };

  const handleTenantChange = (newTenant) => {
    setCurrentTenant(newTenant);
  };

  // Fetch borrowings for children (parent view)
  const fetchChildrenBorrowings = async (children) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get borrowings for all children
      const childIds = children.map(child => child.id || child.student_id);
      const allBorrowings = [];
      
      for (const childId of childIds) {
        try {
          const response = await a2aLibraryService.getBorrowHistory({
            tenant_id: currentTenant,
            reader_id: childId
          });
          if (response.success && response.data) {
            allBorrowings.push(...response.data);
          }
        } catch (err) {
          console.warn(`Error fetching borrowings for child ${childId}:`, err);
        }
      }
      
      setBorrowings(allBorrowings);
      
      // Also fetch books and readers for context
      const [booksResponse, readersResponse] = await Promise.all([
        a2aLibraryService.getBooks({ tenant_id: currentTenant }),
        a2aLibraryService.getReaders({ tenant_id: currentTenant })
      ]);
      
      if (booksResponse.success) {
        setBooks(booksResponse.data || []);
      }
      if (readersResponse.success) {
        setReaders(readersResponse.data || []);
      }
    } catch (err) {
      console.error('Error loading children borrowings:', err);
      setError(`Error loading children borrowings: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const forceRefresh = () => {
    console.log('=== Force Refresh ===');
    setCacheVersion(Date.now());
    fetchLibraryData();
  };

  const renderMainContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading library data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchLibraryData}>Retry</button>
        </div>
      );
    }

    switch (currentView) {
      case 'catalog':
        return (
          <LibraryCatalog
            books={books}
            onBookSelect={handleBookSelect}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            currentTenant={currentTenant}
            onTenantChange={handleTenantChange}
          />
        );
      case 'detail':
        return selectedBook ? (
          <BookDetail
            book={selectedBook}
            onBack={handleBackToCatalog}
            onBorrow={() => setCurrentView('borrowing')}
          />
        ) : null;
      case 'borrowing':
        return (
          <BorrowingSystem
            books={books}
            readers={readers}
            borrowings={borrowings}
            onBack={handleBackToCatalog}
            onBorrowingUpdate={(updatedBorrowings) => setBorrowings(updatedBorrowings)}
            currentTenant={currentTenant}
            onRefresh={fetchLibraryData}
          />
        );
      case 'profile':
        return selectedReader ? (
          <ReaderProfile
            reader={selectedReader}
            borrowings={borrowings.filter(b => b.reader_id === selectedReader.id)}
            books={books}
            onBack={handleBackToCatalog}
          />
        ) : null;
      case 'reports':
        return (
          <LibraryReports
            books={books}
            readers={readers}
            borrowings={borrowings}
            onBack={handleBackToCatalog}
            currentTenant={currentTenant}
          />
        );
      case 'opac':
        return (
          <OPACInterface
            books={books}
            onBookSelect={handleBookSelect}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            currentTenant={currentTenant}
          />
        );
      default:
        return (
          <LibraryCatalog
            books={books}
            onBookSelect={handleBookSelect}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            currentTenant={currentTenant}
            onTenantChange={handleTenantChange}
          />
        );
    }
  };

  return (
    <div className="library-module">
      <div className="library-header">
        <button className="back-button" onClick={onBack}>
          ← 返回
        </button>
        <h1>圖書館管理系統 (Library Master)</h1>
        <div className="header-actions">
          <span className="user-role">圖書館管理員</span>
          <div className="tenant-selector">
            <select 
              value={currentTenant} 
              onChange={(e) => handleTenantChange(e.target.value)}
              className="tenant-select"
            >
              <option value="tn_01">MyClass Real School A</option>
              <option value="tn_02">MyClass Real School B</option>
              <option value="tn_03">MyClass Real School C</option>
            </select>
            <button 
              onClick={forceRefresh}
              className="refresh-button"
              title="強制刷新數據"
            >
              🔄 刷新
            </button>
          </div>
          <button className="search-button">🔍</button>
        </div>
      </div>

      <div className="library-navigation">
        <button
          className={`nav-button ${currentView === 'catalog' ? 'active' : ''}`}
          onClick={() => handleViewChange('catalog')}
        >
          📚 圖書目錄
        </button>
        <button
          className={`nav-button ${currentView === 'borrowing' ? 'active' : ''}`}
          onClick={() => handleViewChange('borrowing')}
        >
          📖 借還書系統
        </button>
        <button
          className={`nav-button ${currentView === 'profile' ? 'active' : ''}`}
          onClick={() => handleViewChange('profile')}
        >
          👤 讀者管理
        </button>
        {showAdminFeatures && (
          <button
            className={`nav-button ${currentView === 'reports' ? 'active' : ''}`}
            onClick={() => handleViewChange('reports')}
          >
            📊 報表系統
          </button>
        )}
        <button
          className={`nav-button ${currentView === 'opac' ? 'active' : ''}`}
          onClick={() => handleViewChange('opac')}
        >
          🌐 OPAC 查詢
        </button>
      </div>

      <div className="library-content">
        {renderMainContent()}
      </div>
    </div>
  );
};

export default LibraryModule;
